import { Alpine } from "../../../../vendor/livewire/livewire/dist/livewire.esm";
import { subscriptions } from "../../wayfinder/routes/api/push";

const applicationServerKey = (vapidPublicKey) => {
    const padding = "=".repeat((4 - (vapidPublicKey.length % 4)) % 4);
    const base64 = (vapidPublicKey + padding).replaceAll("-", "+").replaceAll("_", "/");

    return Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
};

export default () => ({
    supported: false,
    enabled: false,
    pending: false,
    vapidPublicKey: null,
    /**
     * Store the latest user.id + endpoint subscription
     * so the subscription can be reassign if the logged in user change
     */
    identity: Alpine.$persist(null).as("push-notifications.identity"),
    getIdentity(subscription) {
        return window.app.user.id + ":" + subscription.endpoint;
    },
    async init() {
        this.vapidPublicKey = this.getVapidPublicKey();
        this.supported = Boolean(this.vapidPublicKey) && "pushManager" in window;

        if (!this.supported) return;

        this.pending = true;

        try {
            const subscription = await window.pushManager.getSubscription();

            /**
             * Reassign to the new logged in user
             *
             * Old endpoints will be deleted automatically on the backend
             */
            if (subscription && this.identity !== this.getIdentity(subscription)) {
                await this.storeSubscription(subscription);
            }

            this.enabled = Boolean(subscription);
        } finally {
            this.pending = false;
        }
    },
    getVapidPublicKey() {
        return document.head
            .querySelector('meta[name="vapid-public-key"]')
            ?.getAttribute("content");
    },
    async toggle(enabled) {
        if (!this.supported || this.pending) return;

        this.pending = true;

        try {
            if (enabled) {
                await this.subscribe();
            } else {
                await this.unsubscribe();
            }
        } finally {
            this.pending = false;
        }
    },

    async subscribe() {
        const subscription = await window.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: applicationServerKey(this.vapidPublicKey),
        });

        try {
            await this.storeSubscription(subscription);

            this.enabled = true;
        } catch (error) {
            await subscription.unsubscribe();

            this.enabled = false;

            throw error;
        }

        window.dispatchEvent(new CustomEvent("push-subscribe"));

        return subscription;
    },

    async unsubscribe() {
        const subscription = await window.pushManager.getSubscription();

        if (!subscription) {
            return;
        }

        await this.deleteSubscription(subscription);

        try {
            await subscription.unsubscribe();

            this.enabled = false;
        } catch (error) {
            await this.storeSubscription(subscription);

            this.enabled = true;

            throw error;
        }

        window.dispatchEvent(new CustomEvent("push-unsubscribe"));
    },

    async storeSubscription(subscription) {
        const { keys } = subscription.toJSON();

        return window.axios
            .post(subscriptions.url(), {
                endpoint: subscription.endpoint,
                keys,
                content_encoding: PushManager.supportedContentEncodings[0],
            })
            .then((response) => {
                this.identity = this.getIdentity(subscription);
                return response;
            });
    },

    async deleteSubscription(subscription) {
        return window.axios
            .delete(subscriptions.url(), {
                data: {
                    endpoint: subscription.endpoint,
                },
            })
            .then((response) => {
                this.identity = null;
                return response;
            });
    },
});
